import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Brain, 
  Heart, 
  Star, 
  TrendingUp, 
  MessageSquare,
  Lightbulb,
  RefreshCw,
  Users,
  Target
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const PersonalityInsights = () => {
  const [personalityData, setPersonalityData] = useState(null);
  const [matchInsights, setMatchInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  
  const { toast } = useToast();

  useEffect(() => {
    loadPersonalityData();
    loadMatchInsights();
  }, []);

  const loadPersonalityData = async () => {
    try {
      setError('');
      const response = await api.get('/ai-matching/personality');
      setPersonalityData(response.data.analysis);
    } catch (err) {
      setError('Failed to load personality analysis');
      console.error('Personality data error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMatchInsights = async () => {
    try {
      const response = await api.get('/ai-matching/match-insights');
      setMatchInsights(response.data);
    } catch (err) {
      console.error('Match insights error:', err);
      // Provide fallback data
      setMatchInsights({
        insights: ["Start swiping to get personalized insights!"],
        recommendations: ["Complete your profile for better matches"],
        compatibility_trends: "No data available yet",
        suggested_improvements: "Add more photos and interests"
      });
    }
  };

  const refreshAnalysis = async () => {
    setIsRefreshing(true);
    try {
      const response = await api.post('/ai-matching/refresh-personality');
      setPersonalityData(response.data.analysis);
      
      toast({
        title: "Analysis Updated!",
        description: "Your personality analysis has been refreshed with your latest profile changes.",
      });
      
      // Also refresh match insights
      await loadMatchInsights();
      
    } catch (err) {
      toast({
        title: "Refresh Failed",
        description: "Unable to refresh analysis. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const getPersonalityColor = (trait) => {
    const colors = {
      'Friendly': 'bg-blue-100 text-blue-800',
      'Outgoing': 'bg-green-100 text-green-800',
      'Creative': 'bg-purple-100 text-purple-800',
      'Thoughtful': 'bg-indigo-100 text-indigo-800',
      'Active': 'bg-orange-100 text-orange-800',
      'Adventurous': 'bg-red-100 text-red-800',
      'Caring': 'bg-pink-100 text-pink-800',
      'Intelligent': 'bg-teal-100 text-teal-800',
      'Honest': 'bg-gray-100 text-gray-800',
      'Ambitious': 'bg-yellow-100 text-yellow-800'
    };
    return colors[trait] || 'bg-gray-100 text-gray-800';
  };

  const getCompatibilityScore = () => {
    // Simulate compatibility score based on personality completeness
    if (!personalityData) return 0;
    
    const factors = [
      personalityData.personality_traits?.length || 0,
      personalityData.interests?.length || 0,
      personalityData.values?.length || 0,
      personalityData.personality_summary ? 1 : 0
    ];
    
    return Math.min(100, (factors.reduce((a, b) => a + b, 0) / 4) * 20 + 40);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Brain className="h-8 w-8 text-purple-600" />
            Personality Insights
          </h1>
          <p className="text-muted-foreground">
            AI-powered analysis of your dating profile and matching patterns
          </p>
        </div>
        
        <Button 
          onClick={refreshAnalysis}
          disabled={isRefreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Updating...' : 'Refresh Analysis'}
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-red-800">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      {personalityData && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Personality Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-600" />
                Personality Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              
              {/* Profile Completeness */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Profile Strength</span>
                  <span>{getCompatibilityScore()}%</span>
                </div>
                <Progress value={getCompatibilityScore()} className="h-2" />
              </div>

              {/* Personality Traits */}
              <div>
                <h4 className="text-sm font-semibold mb-2">Top Personality Traits</h4>
                <div className="flex flex-wrap gap-2">
                  {personalityData.personality_traits?.slice(0, 5).map((trait, index) => (
                    <Badge key={index} className={getPersonalityColor(trait)}>
                      {trait}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Communication Style */}
              {personalityData.communication_style && (
                <div>
                  <h4 className="text-sm font-semibold mb-1">Communication Style</h4>
                  <p className="text-sm text-muted-foreground">
                    {personalityData.communication_style}
                  </p>
                </div>
              )}

              {/* Values */}
              {personalityData.values?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Core Values</h4>
                  <div className="flex flex-wrap gap-1">
                    {personalityData.values.map((value, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {value}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Interests & Lifestyle */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-red-600" />
                Interests & Lifestyle
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              
              {/* Interests */}
              {personalityData.interests?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Top Interests</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {personalityData.interests.map((interest, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        {interest}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lifestyle */}
              {personalityData.lifestyle && (
                <div>
                  <h4 className="text-sm font-semibold mb-1">Lifestyle</h4>
                  <p className="text-sm text-muted-foreground">
                    {personalityData.lifestyle}
                  </p>
                </div>
              )}

              {/* Relationship Goals */}
              {personalityData.relationship_goals && (
                <div>
                  <h4 className="text-sm font-semibold mb-1">Relationship Goals</h4>
                  <p className="text-sm text-muted-foreground">
                    {personalityData.relationship_goals}
                  </p>
                </div>
              )}

              {/* Compatibility Keywords */}
              {personalityData.compatibility_keywords?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Compatibility Keywords</h4>
                  <div className="flex flex-wrap gap-1">
                    {personalityData.compatibility_keywords.map((keyword, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        #{keyword}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Match Insights */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Match Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              
              {/* Insights */}
              {matchInsights?.insights?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                    <Lightbulb className="h-4 w-4" />
                    Key Insights
                  </h4>
                  <div className="space-y-2">
                    {matchInsights.insights.slice(0, 3).map((insight, index) => (
                      <div key={index} className="bg-blue-50 p-2 rounded text-sm text-blue-800">
                        • {insight}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {matchInsights?.recommendations?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                    <Target className="h-4 w-4" />
                    Recommendations
                  </h4>
                  <div className="space-y-2">
                    {matchInsights.recommendations.slice(0, 3).map((rec, index) => (
                      <div key={index} className="bg-green-50 p-2 rounded text-sm text-green-800">
                        ✓ {rec}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Compatibility Trends */}
              {matchInsights?.compatibility_trends && (
                <div>
                  <h4 className="text-sm font-semibold mb-1">Compatibility Trends</h4>
                  <p className="text-sm text-muted-foreground">
                    {matchInsights.compatibility_trends}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Personality Summary */}
      {personalityData?.personality_summary && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-600" />
              AI Personality Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg">
              <p className="text-purple-800 leading-relaxed">
                {personalityData.personality_summary}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Suggested Improvements */}
      {matchInsights?.suggested_improvements && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-600" />
              Profile Optimization Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-orange-50 p-4 rounded-lg">
              <p className="text-orange-800">
                <strong>Suggestion:</strong> {matchInsights.suggested_improvements}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PersonalityInsights;